/**
 * Runs contract (F18, milestone M3 slice): starting, listing and reading runs
 * with their steps, counts, warnings, search log and PRISMA numbers so far.
 * Re-exported from index.ts.
 *
 * Owner: run orchestration (F18). Shapes follow
 * specs/features/F18-living-updates/design.md, Interfaces, with RunResponse.prisma
 * added for api.md E27 ("steps, counts, PRISMA numbers"). Schedules and
 * candidates are in living.ts.
 */
import type { PrismaDeduplication } from './dedup';
import { pagePath } from './paths';
import type { ExtractionRunWarningCode } from './extraction';
import type { RobRunWarningCode } from './rob';
import type { ScreeningRunWarningCode, PrismaScreening } from './screening';
import type { ApiSearchSource, PrismaIdentification, SearchLogEntry, SearchRunWarningCode } from './search';

export const RUN_ROUTES = {
  /** POST StartRunRequest -> 202 RunResponse (E25; LIVE-030, LIVE-031). GET ?limit&cursor -> 200 RunListResponse (E26; LIVE-037). */
  runs: (slug: string) => `${pagePath(slug)}/runs`,
  /** GET -> 200 RunResponse (E27; LIVE-037). */
  run: (slug: string, runId: string) => `${pagePath(slug)}/runs/${encodeURIComponent(runId)}`,
  /** POST -> 200 RunResponse (E28; LIVE-038). */
  runCancel: (slug: string, runId: string) => `${pagePath(slug)}/runs/${encodeURIComponent(runId)}/cancel`,
  /** POST -> 200 RunResponse; 409 conflict with details.reason 'run_not_stalled' (E116; LIVE-125). */
  runResume: (slug: string, runId: string) => `${pagePath(slug)}/runs/${encodeURIComponent(runId)}/resume`,
  /** GET -> 200 RunEstimate for a full run (E117; LIVE-127 to LIVE-129). */
  runEstimate: (slug: string) => `${pagePath(slug)}/runs/estimate`,
} as const;

export type RunKind = 'full' | 'confirmation';
export type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type RunSource = 'scheduler' | 'manual' | 'import';

/** Stage order of a full run (LIVE-040); 'fail' closes a run whose step failed after its last attempt (LIVE-055). */
export const FULL_RUN_STAGES = [
  'start',
  'search',
  'dedupe',
  'screen',
  'retrieve',
  'extract',
  'rob',
  'retractions',
  'analyse',
  'notify',
  'finish',
] as const;

/** Stage order of a confirmation run (LIVE-041). */
export const CONFIRMATION_RUN_STAGES = ['start', 'retractions', 'analyse', 'notify', 'finish'] as const;

export type RunStage = (typeof FULL_RUN_STAGES)[number] | 'fail';

/** Stages run by a feature's StageRunner (LIVE-042): search F13, dedupe F14, screen F15, retrieve and extract F16, rob and retractions F17. */
export type PipelineStage = 'search' | 'dedupe' | 'screen' | 'retrieve' | 'extract' | 'rob' | 'retractions';

export type RunStepStatus = 'running' | 'completed' | 'failed' | 'skipped';

/** LIVE-036. Every field is a count for this run. */
export interface RunCounts {
  recordsIdentified: number;
  recordsNew: number;
  duplicatesLinked: number;
  screenedTitleAbstract: number;
  awaitingHumanTitleAbstract: number;
  fullTextRetrieved: number;
  fullTextNotRetrieved: number;
  screenedFullText: number;
  awaitingHumanFullText: number;
  valuesProposed: number;
  robProposed: number;
  retractionsFound: number;
  candidatesPending: number;
  modelCalls: number;
}

export type RunWarningCode = 'served_model_differs' | SearchRunWarningCode | ScreeningRunWarningCode | ExtractionRunWarningCode | RobRunWarningCode;

export interface RunWarning {
  code: RunWarningCode;
  source?: string;
  detail?: string;
}

/** One run_step row (LIVE-037). chunk 0 is a plan step or a single-step stage. */
export interface RunStepView {
  stage: RunStage;
  chunk: number;
  status: RunStepStatus;
  attempt: number;
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
}

/** Pinned configuration of one model purpose for a run (LIVE-033; AI-035). served is present only when it differs from [model]. */
export interface RunModelVersion {
  provider: 'anthropic';
  model: string;
  promptVersion: string;
  served?: string[];
}

/** LIVE-034. */
export interface RunEngineVersions {
  stats: string;
  evidenceModel: string;
  commit: string;
}

/**
 * PRISMA numbers for one run, composed from the parts each stage owns:
 * identification (F13), deduplication (F14) and screening (F15). A part is
 * null until its stage is built. Frozen into run.prisma_json at finish; read
 * live while the run is active. F20 composes the full PrismaCounts later.
 */
export interface RunPrisma {
  scope: { runId: string } | 'cumulative';
  identification: PrismaIdentification | null;
  deduplication: PrismaDeduplication | null;
  screening: PrismaScreening | null;
}

export interface RunResponse {
  id: string;
  kind: RunKind;
  source: RunSource;
  status: RunStatus;
  scheduledFor: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** 'user:<id>', 'system', or null for scheduler and import runs. */
  triggeredBy: string | null;
  error: string | null;
  /**
   * LIVE-122: queued or running, with no live lease and no step enqueued, finished or
   * retried for 5 minutes. The status stays queued or running; the run resumes by itself
   * (LIVE-123, LIVE-124) or through RUN_ROUTES.runResume (LIVE-125). False once finished.
   */
  stalled: boolean;
  /** Keyed by F22 purpose; {} for import runs. Never shown in the web app (CONT-058). */
  modelVersions: Record<string, RunModelVersion>;
  engineVersions: RunEngineVersions | null;
  searchLog: SearchLogEntry[];
  /** Summed over completed steps so far; final once the run has finished. Null for import runs. */
  counts: RunCounts | null;
  warnings: RunWarning[];
  /** Null for import runs. */
  prisma: RunPrisma | null;
  snapshotId: string | null;
  /**
   * What the analyse step found (LIVE-062, LIVE-093): 'new_snapshot' when the confirmed evidence changed and a
   * snapshot was written, 'no_change' when it did not, null when the run never reached the step (an older run,
   * or one that failed or was cancelled first). Optional only so fixtures from before Phase 3 stay valid.
   */
  analysis?: RunAnalysisOutcome | null;
  /** In execution order. */
  steps: RunStepView[];
  /** One entry per stage of the run's kind, in stage order (LIVE-110); [] for import runs. Not in RunSummary: read E27 for an active run's progress. */
  progress: RunStageProgress[];
}

/**
 * Where a stage of a run has got to (LIVE-110). `waiting`: not reached yet;
 * `planning`: its plan step is under way; `running`: planned, chunks under way;
 * `completed`: every chunk done; `skipped`: nothing to do, or not built in this
 * release; `failed`: the run failed in this stage.
 */
export type RunStageStatus = 'waiting' | 'planning' | 'running' | 'completed' | 'skipped' | 'failed';

/** One stage of a run's kind, in stage order (LIVE-110). Single-step stages (start, analyse, notify, finish) have one chunk and no units. */
export interface RunStageProgress {
  stage: Exclude<RunStage, 'fail'>;
  /** False when the stage's runner is a placeholder in this release: it is always skipped. */
  available: boolean;
  status: RunStageStatus;
  chunksDone: number;
  /** Null until the stage is planned; 0 for a stage whose plan had no units. */
  chunksTotal: number | null;
  unitsDone: number;
  /** Null until the stage is planned, and for single-step stages. */
  unitsTotal: number | null;
}

/** LIVE-093: the Snapshot column of the run history. */
export type RunAnalysisOutcome = 'new_snapshot' | 'no_change';

export type RunSummary = Omit<RunResponse, 'steps' | 'searchLog' | 'progress'>;

/** GET RUN_ROUTES.runs: newest first, limit 1 to 200 (default 50). */
export interface RunListResponse {
  runs: RunSummary[];
  nextCursor: string | null;
}

/** POST RUN_ROUTES.runs. Exactly this key. */
export interface StartRunRequest {
  kind: RunKind;
}

/**
 * details.reason of a 409 conflict when starting a run: the page cannot run
 * (LIVE-014 order; SRCH-013, SRCH-014), or a run is already active (LIVE-031,
 * with details.activeRunId).
 */
export type RunConflictReason = 'protocol_not_frozen' | 'not_pipeline_page' | 'strategy_not_approved' | 'run_active';

/** details.reason of a 409 conflict from RUN_ROUTES.runResume: the run is moving, or has finished (LIVE-125). */
export type RunResumeConflictReason = 'run_not_stalled';

/** A range of whole numbers or US dollars, low <= high. */
export interface EstimateRange {
  low: number;
  high: number;
}

/**
 * One enabled source of a full run's estimate (LIVE-127). Hit counts come from the
 * latest preview of the approved strategy version (SRCH-037); the estimate never
 * contacts a source itself.
 */
export interface RunEstimateSource {
  source: ApiSearchSource;
  /** The approved version the run will search; null when none is approved (the run cannot start, SRCH-014). */
  approvedVersion: number | null;
  /** Hits reported by the latest preview of the approved version; null when it has not been counted. */
  hitCount: number | null;
  countedAt: string | null;
  /**
   * 'full': every date up to the run date. 'incremental': from dateFrom only (SRCH-102), so the
   * preview, which counts every date, is an upper bound and the low end is 0.
   */
  window: { kind: 'full' | 'incremental'; dateFrom: string | null; dateTo: string };
  /** Records this source is expected to return, at most the per-source limit; null when not counted. */
  expected: EstimateRange | null;
  /** True when the hits exceed the per-source limit, so the search stops at `limit` records. */
  capped: boolean;
  limit: number;
}

/** Why a run needs an explicit confirmation before it starts (LIVE-129). */
export type RunEstimateReason = 'records' | 'cost' | 'daily_cap' | 'not_counted' | 'plan_limit';

/** GET RUN_ROUTES.runEstimate (E117; LIVE-127 to LIVE-129). Amounts in US dollars, rounded to the cent. */
export interface RunEstimate {
  kind: 'full';
  /** The run date every search is limited to (LIVE-044). */
  referenceDate: string;
  sources: RunEstimateSource[];
  /** False when an enabled source has no count: the totals then cover the counted sources only. */
  complete: boolean;
  /** Records the searches are expected to return, summed over counted sources. */
  records: EstimateRange;
  /** Records left after duplicate removal, assuming `duplicateShare` of them are duplicates (LIVE-128). */
  afterDuplicates: EstimateRange & { duplicateShare: EstimateRange };
  screening: {
    /** Records already waiting for AI screening (SCRN-096), screened by this run too. */
    awaitingNow: { titleAbstract: number; fullText: number };
    /** Title-and-abstract records this run is expected to screen: awaitingNow plus afterDuplicates. */
    records: EstimateRange;
    passes: 2;
    usd: EstimateRange;
    /** Token use per call: the page's recent screening calls, or the F22 defaults (SCRN-096). */
    basis: 'page_history' | 'defaults';
  };
  /** The workspace's daily AI spending limit (AI-074). */
  dailyCap: { capUsd: number; spentTodayUsd: number; remainingUsd: number; mayBeReached: boolean };
  thresholds: { records: number; usd: number };
  /**
   * The workspace plan's AI-screened record allowance (F24 BILL-085): records left in the allowance
   * window, and whether this run may use them up. Absent when billing is not enforced.
   */
  planAllowance?: { allowance: number; used: number; remaining: number; trial: boolean; mayBeReached: boolean };
  /** True when Run now must ask for an explicit confirmation (LIVE-129, LIVE-131). */
  needsConfirmation: boolean;
  reasons: RunEstimateReason[];
}
